import LoadingButton from '@mui/lab/LoadingButton';
import cc from 'classcat';
import * as z from 'zod';
import * as CMR from '../../constants/community-roles';
import { useRecoilState } from 'recoil';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import compact from 'lodash/compact';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ControlLabel from 'components/control-label/ControlLabel';
import React, {
  ReactChild,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  useForm,
  useWatch,
  UseFormRegister,
  Control,
} from 'react-hook-form';
import { reportServerError } from 'utils/report-error';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import C from './SpaceEditModal.css';
import ImageUploadPreview from '../image-upload-preview/ImageUploadPreview';
import MultiAutocomplete, {
  ListOption,
} from 'components/multi-autocomplete/MultiAutocomplete';
import { match, P } from 'ts-pattern';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandButton from 'components/buttons/ExpandButton';
import DateTimeComboPicker from 'components/date-time-combo-picker/DateTimeComboPicker';
import TimeDurationInputCombo, {
  addDuration,
  getDuration,
  TimeDuration,
} from 'components/time-duration-input-combo/TimeDurationInputCombo';
import SearchInputField from 'components/search-input-field/SearchInputField';
import {
  AddMembersButton,
  UserList,
} from 'components/community-members-explorer/CommunityMembersExplorer';
import { useCommunityDetails } from 'atoms/community-details';
import uniq from 'lodash/uniq';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import ParticipantsIcon from '!!react-svg-loader!./form-icons/icon--space-participants.svg';
import startTimeIcon from './form-icons/icon--space-start-time.svg';
import spaceTypeIcon from './form-icons/icon--space-type.svg';
import vidSettingsIcon from './form-icons/icon--space-video-settings.svg';
import audioSettingsIcon from './form-icons/icon--space-audio-settings.svg';
import meetingSettingsIcon from './form-icons/icon--meeting-settings.svg';
import CoCoBlocksIcon from './form-icons/icon--space-cocoblocks.svg';
import CoCoJSIcon from './form-icons/icon--space-cocojs.svg';
import CoCoCanvasIcon from './form-icons/icon--space-cococanvas.svg';
import CoCoStoriesIcon from './form-icons/icon--space-cocostories.svg';
import Switch from '@mui/material/Switch';
import { limitChangeLength } from 'utils/form';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useUserProfile } from 'atoms/user-profile';
import { useCommunitySummaries } from 'atoms/community-summaries';
import {
  CommunityWithAggregates,
  Space,
  CommunityUserGroup,
  SpaceType,
  CommunityDetails,
} from '@cocoplatform/coco-rtc-shared';
import ToggleChip from 'components/toggle-chip/ToggleChip';
import isEmpty from 'lodash/isEmpty';
import { useUserPref } from 'atoms/user-preferences';
import { qualifiedUserName } from 'utils/user';
import { useThemeSelection } from 'atoms/space-themes';
import { spaceMappingAtom, useSpaceSummary } from 'atoms/spaces';
import CloseableDialog from 'components/modal-dialog/CloseableDialog';
import { isEqual } from 'lodash';
import { usePreviousDifferent, useWindowSize } from 'rooks';
import { CommunityIcon } from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import { ScrollableContainer } from 'components/scrollable-container/ScrollableContainer';
import BlockedFeature, {
  BLOCK_TYPE,
} from 'components/blocked-feature/blocked-feature';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import spacePng from './space-intro.png';
import { Help, PersonAddAlt } from '@mui/icons-material';
import { EVENTS, INTERACTIONS, useTrack } from 'utils/mixpanel';
import { useFlags } from 'utils/feature-flags';

const maxTitleLen = 50;
const maxDescLen = 180;
const maxSearchHeight = 125;

const SPACE_ONBOARDING = 'space-onboarding';

export const getOnboardingDone = () => {
  return localStorage.getItem('space-onboarding-done') === 'true';
};

export const setOnboardingDone = () => {
  localStorage.setItem('space-onboarding-done', 'true');
};

export const getSpaceTypePreference = () => {
  return localStorage.getItem('space-type-preference') as SpaceType;
};

export const setSpaceTypePreference = (spaceType: SpaceType) => {
  localStorage.setItem('space-type-preference', spaceType);
};

export const spaceTypes = [
  {
    id: 'scratch_blocks' as SpaceType,
    label: (
      <div>
        {' '}
        <img src={CoCoBlocksIcon} className={C.spaceTypeMenuIcons} />{' '}
        <Trans>CoCo Blocks</Trans>{' '}
        <span className={C.spaceTypeSubtext}>
          {' '}
          — <Trans>Co-create Scratch projects</Trans>
        </span>
      </div>
    ),
  },
  {
    id: 'p5_text' as SpaceType,
    label: (
      <div>
        {' '}
        <img src={CoCoJSIcon} className={C.spaceTypeMenuIcons} />{' '}
        <Trans>CoCo JS</Trans>{' '}
        <span className={C.spaceTypeSubtext}>
          {' '}
          — <Trans>Co-create p5.js projects</Trans> {/* Make art together */}
        </span>
      </div>
    ),
  },
  {
    id: 'canvas' as SpaceType,
    label: (
      <div>
        {' '}
        <img src={CoCoCanvasIcon} className={C.spaceTypeMenuIcons} />{' '}
        <Trans>CoCo Canvas</Trans>{' '}
        <span className={C.spaceTypeSubtext}>
          {' '}
          — <Trans>Coming Soon</Trans> {/* Make art together */}
        </span>
      </div>
    ),
  },
  {
    id: 'stories' as SpaceType,
    label: (
      <div>
        {' '}
        <img src={CoCoStoriesIcon} className={C.spaceTypeMenuIcons} />{' '}
        <Trans>CoCo Stories</Trans>{' '}
        <span className={C.spaceTypeSubtext}>
          {' '}
          — <Trans>Coming Soon</Trans> {/* Write stories together */}
        </span>
      </div>
    ),
  },
].map((it) => ({
  ...it,
  isEnabled: ENABLED_SPACE_TYPES.indexOf(it.id) >= 0,
}));

// const meetingModes: ListOption[] = [
//   {
//     id: 'everyone_same_room',
//     label: 'Everyone in the same room',
//   },
//   {
//     id: 'everyone_online',
//     label: 'Everyone online',
//   },
//   {
//     id: 'mix_online',
//     label: 'Some in the same room, some online',
//   },
// ];

const UserIdSelectionSchema = z.object({
  userIds: z.string().array(),
});

type UserIdSelection = z.TypeOf<typeof UserIdSelectionSchema>;

const useUserSelection = (opts: {
  defaultSelectedUserIds?: string[];
  spaceId?: string;
  communityId?: string;
}) => {
  const { userProfile } = useUserProfile();

  const [defaultSelectedUserIds, setDefaultSelectedUserIds] = useState(
    opts.defaultSelectedUserIds ?? (userProfile ? [userProfile.id] : []),
  );

  const [selectedUserIds, _setSelectedUserIds] = useState<string[]>(
    defaultSelectedUserIds,
  );

  const [didSelectUsers, setSelectedUsers] = useState(false);

  const [isTainted, setTainted] = useState(false);

  useEffect(() => {
    if (opts.spaceId && selectedUserIds.length > 0 && !didSelectUsers) {
      setSelectedUsers(true);
    }
  }, [opts.spaceId, selectedUserIds]);

  const setSelectedUserIds: typeof _setSelectedUserIds = (...args) => {
    _setSelectedUserIds(...args);
    setSelectedUsers(true);
    setTainted(true);
  };

  return {
    isTainted,
    setTainted,
    selectedUserIds,
    setSelectedUserIds,
    defaultSelectedUserIds,
    didSelectUsers,
  };
};

function NoCorridorMembers({
  onSkip,
  communityId,
}: {
  onSkip: (didAdd: boolean) => void;
  communityId: string;
}) {
  const didAdd = useRef(false);
  return (
    <>
      <Stack p={4} alignItems='center' textAlign='center'>
        <ParticipantsIcon
          style={{
            width: '3rem',
            fill: 'var(--color-primary)',
          }}
        />
        <Typography mt={3} variant='h2'>
          <Trans>
            You have not invited any members
            <br /> in this community yet.
          </Trans>
        </Typography>
        <Typography my={3} variant='body2'>
          <Trans>
            CoCo is especially designed for collaborative creative experiences
            with peers. Invite others to experience the power and joy of
            <br />
            <span
              style={{
                fontWeight: 'bold',
                color: 'var(--color-text)',
              }}
            >
              Being. Creative. Together.
            </span>
          </Trans>
        </Typography>
        <AddMembersButton
          communityId={communityId}
          onCloseModal={() => {
            onSkip(didAdd.current);
          }}
          onSubmit={() => {
            didAdd.current = true;
          }}
        />
      </Stack>
      <Box p={2}>
        <Divider />
        <Stack mt={2} direction='row' justifyContent='flex-end'>
          <Button
            data-analytics={EVENTS.CREATE_SPACE_ADD_MEMBER_SKIP_FOR_NOW}
            variant='outlined'
            onClick={() => {
              onSkip(true);
            }}
          >
            <Trans>Skip for now</Trans>
          </Button>
        </Stack>
      </Box>
    </>
  );
}

function SpaceOnboardingModal({ onNext }: { onNext: () => void }) {
  const track = useTrack();

  const { userProfile, updateUserProfile } = useUserProfile();

  return (
    <>
      <ModalHeader
        title={
          <Trans>
            Co-create, Code, Collaborate —{' '}
            <span style={{ fontWeight: '900' }}>WITH PEERS IN REAL-TIME!</span>
          </Trans>
        }
      />
      <Stack p={2} minWidth='500px'>
        <img
          src={spacePng}
          style={{
            width: '400px',
            alignSelf: 'center',
          }}
        />
        <Typography mt={3} variant='h3'>
          <Trans>What are CoCo Spaces?</Trans>
        </Typography>
        <Typography variant='body2'>
          <Trans>
            <ul className={C.unindentList}>
              <li>
                CoCo spaces provide a shared collaborative coding environment
                for a group of members to co-create projects together in
                real-time.
              </li>
              <li>
                Create any number of spaces with peers. All spaces are saved in
                the corridor.
              </li>
            </ul>
          </Trans>
        </Typography>
        <Typography variant='h3'>
          <Trans>Steps for Getting Started:</Trans>
        </Typography>
        <Typography variant='body2'>
          <Trans>
            <ul className={C.unindentList}>
              <li>
                Form a group of 2 or more members. Ask one member to start a new
                space.
              </li>
              <li>
                If you are starting a space, click next to select others as
                co-creators. They will get an invite notification to join the
                same space.
              </li>
            </ul>
          </Trans>
        </Typography>
        <Button
          sx={{
            alignSelf: 'flex-end',
          }}
          variant='contained'
          color='primary'
          onClick={() => {
            track(EVENTS.CREATE_SPACE_NEXT, INTERACTIONS.CLICK, {
              step: 'onboarding',
            });

            updateUserProfile({
              tutorialsCompleted: uniq([
                ...(userProfile?.tutorialsCompleted ?? []),
                SPACE_ONBOARDING,
              ]),
            });

            onNext();
          }}
        >
          <Trans>Next</Trans>
        </Button>
      </Stack>
      <Box></Box>
    </>
  );
}

export default function InternalSpaceEditModal(p: {
  headerTitle?: string;
  submitLabel?: string;
  defaultCommunityId?: string;
  defaultBannerUrl?: string | null;
  onClose?: () => void;
  onSubmit: (formData: FormData, input: any) => Promise<Space>;
  defaultValues?: any;
  spaceId?: string;
  defaultSelectedUserIds?: string[];
  defaultMandatoryUserIds?: string[];
  rememberUserSelection?: boolean;
  initialView?: 'primary-form' | 'participant-selection' | 'av-settings';
  forceShowCommunitySelector?: boolean;
}) {
  const { _ } = useLingui();
  const {
    communities,
    syncState: summarySyncState,
    trigger,
  } = useCommunitySummaries({
    autoLoad: false,
  });

  const hostableCommunities = useMemo(() => {
    return selectHostableCommunities(communities);
  }, [communities]);

  const showCommunitySelector =
    (p.forceShowCommunitySelector && hostableCommunities.length > 1) ||
    (!p.spaceId && !p.defaultCommunityId && hostableCommunities.length > 1);

  const defaultCommunityId = useMemo(() => {
    if (
      p.defaultCommunityId &&
      hostableCommunities.find((it) => it.id === p.defaultCommunityId)
    ) {
      return p.defaultCommunityId;
    }
    return hostableCommunities[0]?.id;
  }, [hostableCommunities, p.defaultCommunityId]);

  const [, setSpaceMapping] = useRecoilState(spaceMappingAtom);
  const { userProfile, updateUserProfile } = useUserProfile();
  const { innerWidth, innerHeight } = useWindowSize();
  const [step, setStep] = useState<'primary' | 'aux'>('primary');

  const track = useTrack();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues: getValue,
  } = useForm({
    defaultValues: {
      ...p.defaultValues,
      endsIn: normalizeDuration(
        getDuration(
          p.defaultValues?.expectedToStartAt,
          p.defaultValues?.expectedToEndAt,
        ),
        !!p.defaultValues?.expectedToEndAt,
      ),
      communityId: defaultCommunityId,
    },
  });

  const [bannerImage, setBannerImage] = useState<File | null>(null);

  const [startTimeExpanded, setStartTimeExpanded] = useState(
    p.defaultValues.expectedToStartAt != null,
  );
  const [endTimeExpanded, setEndTimeExpanded] = useState(
    p.defaultValues.expectedToEndAt != null,
  );
  const title = useWatch({ name: 'title', control });
  const description = useWatch({ name: 'description', control });
  const communityId = useWatch({ name: 'communityId', control });
  const spaceType = useWatch({ name: 'spaceType', control });
  const openForOthers = useWatch({ name: 'openForOthers', control });

  const defaultSpaceTypePreference = useMemo(() => {
    return getSpaceTypePreference();
  }, []);

  const {
    community,
    canInviteCollaborators,
    canChangeAudioSettings,
    canChangeVideoSettings,
    canCrossGroupCollab,
  } = useCommunityDetails({
    communityId,
    autoLoad: true,
  });

  const noMembersInCorridor = community?.members?.length === 1;
  const canManageUsers = useMemo(
    () => !!community?.roles?.find((it) => it === 'OWNER' || it === 'MANAGER'),
    [community],
  );

  const [activeView, setActiveView] = useState<
    | 'primary-form'
    | 'participant-selection'
    | 'av-settings'
    | 'space-onboarding'
    | 'no-corridor-members'
  >(
    (p.initialView === 'participant-selection' &&
      canManageUsers &&
      noMembersInCorridor &&
      'no-corridor-members') ||
    p.initialView ||
    (p.spaceId
      ? 'primary-form'
      : userProfile?.tutorialsCompleted?.includes(SPACE_ONBOARDING)
        ? canInviteCollaborators
          ? canManageUsers && noMembersInCorridor
            ? 'no-corridor-members'
            : 'participant-selection'
          : 'primary-form'
        : 'space-onboarding'),
  );

  const isHost = CMR.isHost(community?.roles ?? []);
  const mandatoryUserIds =
    p.defaultMandatoryUserIds ||
    ((userProfile && !isHost) || !canInviteCollaborators
      ? [userProfile?.id]
      : []);
  const maxParticipantCount = isHost ? 12 : 6;

  useEffect(() => {
    // set preference on local storage
    if (spaceType) {
      setSpaceTypePreference(spaceType);
    }
  }, [spaceType, community]);

  useEffect(() => {
    // Recompute initial view based on the community
    if (activeView === 'no-corridor-members' && !noMembersInCorridor) {
      setActiveView('participant-selection');
    } else if (noMembersInCorridor) {
      setActiveView('no-corridor-members');
    }
  }, [community]);

  useEffect(() => {
    if (summarySyncState === 'pending') {
      trigger();
    }
  }, []);

  const { selectedUserIds, setSelectedUserIds, didSelectUsers } =
    useUserSelection({
      communityId,
      spaceId: p.spaceId,
      defaultSelectedUserIds: p.defaultSelectedUserIds,
    });
  const vidEnabledForParticipants = useWatch({
    name: 'vidEnabledForParticipants',
    control,
  });
  const micEnabledForParticipants = useWatch({
    name: 'micEnabledForParticipants',
    control,
  });

  const previousNumSelectedUserIds = usePreviousDifferent(
    selectedUserIds.length,
  );

  useEffect(() => {
    if (previousNumSelectedUserIds === 1 && selectedUserIds.length === 2) {
      setValue('openForOthers', false);
    }

    if (previousNumSelectedUserIds === 2 && selectedUserIds.length === 1) {
      setValue('openForOthers', true);
    }
  }, [previousNumSelectedUserIds]);

  const meetingMode = useWatch({ name: 'meetingMode', control });

  const avChanged = useMemo(() => {
    return (
      [micEnabledForParticipants, vidEnabledForParticipants].findIndex(
        (it) => !it,
      ) >= 0
    );
  }, [meetingMode, micEnabledForParticipants, vidEnabledForParticipants]);

  const [isDispatching, setDispatching] = useState(false);
  const { replaceLocal: replaceLocalSpace, space } = useSpaceSummary({
    spaceId: p.spaceId,
  });
  const { internalThemes } = useThemeSelection({
    communityId: p.defaultCommunityId,
    autoLoad: false,
  });
  const themeOptions = useMemo((): ListOption[] => {
    return internalThemes.map((it) => {
      return {
        id: it.name,
        label: it.name,
      };
    });
  }, [internalThemes]);

  const performSubmit = async (data: any) => {
    setDispatching(true);
    const formData = new FormData();
    const { themes, endsIn, openForOthers, ...input } = data;
    if (endTimeExpanded && endsIn) {
      const endTime = addDuration(
        input.expectedToStartAt ?? new Date(),
        endsIn,
      );
      if (endTime) input.expectedToEndAt = +endTime;
    }
    if (themes) {
      input.themes = compact(themes.map((it: ListOption) => it.id));
    }
    if (didSelectUsers) {
      const selectedSet = new Set(selectedUserIds);
      input.userIds = compact(
        community?.members?.map((m) => {
          const userId = m.user?.id;
          if (userId && selectedSet.has(userId)) return userId;
          return null;
        }),
      );
    }
    input.communityId ??= p.defaultCommunityId;

    // Flip the inviteOnly flag as input is the opposite of the actual flag
    // If the user does not have the permission to invite collaborators, the space is invite only
    input.inviteOnly =
      canInviteCollaborators && canCrossGroupCollab ? !openForOthers : true;

    input.vidEnabledForHost = input.vidEnabledForParticipants;
    input.micEnabledForHost = input.micEnabledForParticipants;

    if (!canChangeVideoSettings) {
      input.vidEnabledForHost = false;
      input.vidEnabledForParticipants = false;
    }

    if (!canChangeAudioSettings) {
      input.micEnabledForHost = false;
      input.micEnabledForParticipants = false;
    }

    formData.append('input', JSON.stringify(input));
    if (bannerImage) {
      formData.append('banner', bannerImage);
    }
    try {
      const space = await p.onSubmit(formData, input);
      setSpaceMapping((s) => ({
        ...s,
        [space.id]: space,
      }));
      replaceLocalSpace(space);
    } catch (error) {
      reportServerError({
        title: _(msg`Failed to save space`),
        error,
      });
    } finally {
      setDispatching(false);
    }
  };

  const now = +new Date();

  const canChangeStart =
    ENABLE_SPACE_BOUNDARY_TIMES &&
    !(space && space.startedAt && space.startedAt < now);

  const { p5_enabled: p5Enabled } = useFlags(['p5_enabled']);

  const canChangeNonDescriptionFields = !(
    space &&
    space.endedAt &&
    space.endedAt < now
  );
  const auxFields = match(activeView)
    .with('space-onboarding', () => {
      return (
        <SpaceOnboardingModal
          onNext={() => {
            setActiveView(
              canInviteCollaborators
                ? canManageUsers && noMembersInCorridor
                  ? 'no-corridor-members'
                  : 'participant-selection'
                : 'primary-form',
            );
          }}
        />
      );
    })
    .with('no-corridor-members', () => {
      return (
        <>
          <ModalHeader
            rightControls={
              <Stack justifyContent='center'>
                <Help
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setActiveView('space-onboarding');
                  }}
                />
              </Stack>
            }
            title={<Trans>Select Co-creators</Trans>}
            {...(showCommunitySelector
              ? {
                community,
                communities: hostableCommunities,
                setCommunity: (c) => {
                  setValue('communityId', c.id);
                },
              }
              : {})}
          />
          <NoCorridorMembers
            onSkip={(didAdd) => {
              if (!didAdd) {
                return;
              }
              setActiveView(
                canInviteCollaborators
                  ? 'participant-selection'
                  : 'primary-form',
              );
            }}
            communityId={communityId}
            community={community}
            showCommunitySelector={showCommunitySelector}
          />
        </>
      );
    })
    .with('primary-form', () => {
      return (
        <>
          <ModalHeader
            onBack={
              canInviteCollaborators
                ? () => {
                  setActiveView('participant-selection');
                }
                : undefined
            }
            {...(showCommunitySelector
              ? {
                community,
              }
              : {})}
            title={
              p.headerTitle ??
              (p.spaceId
                ? _(msg`Edit space details`)
                : _(msg`Enter space details`))
            }
          />
          <div>
            <div className={C.bodyInner}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ParticipantsIcon className={C.secIcon} />
                  <ControlLabel className={C.subHeader}>
                    <Trans>Select co-creators:</Trans>
                  </ControlLabel>
                </Grid>
                {/* {showCommunitySelector ? (
                  <Grid item xs={6}>
                    <Controller
                      name='communityId'
                      {...{ control, ...register }}
                      render={({ field }) => (
                        <Select
                          style={{
                            textAlign: 'left',
                            color: 'var(--color-subtext)',
                          }}
                          variant='outlined'
                          size='small'
                          fullWidth
                          placeholder='Select a Community'
                          {...field}
                          disabled={!canChangeNonDescriptionFields}
                        >
                          {hostableCommunities.map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Grid>
                ) : null} */}
                <Grid
                  item
                  xs={12}
                  {...({
                    disabled: !canChangeNonDescriptionFields,
                  } as any)}
                >
                  <ExpandButton
                    className={C.mutedOutlineBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveView('participant-selection');
                    }}
                    disabled={
                      !canChangeNonDescriptionFields || !canInviteCollaborators
                    }
                  >
                    {getMemberCountText(
                      community,
                      userProfile,
                      didSelectUsers,
                      selectedUserIds,
                      mandatoryUserIds,
                      openForOthers,
                      canInviteCollaborators,
                      canCrossGroupCollab,
                    )}
                  </ExpandButton>
                </Grid>
              </Grid>
            </div>
            {/* Space Title */}
            <div className={C.bodyInner}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <img src={spaceTypeIcon} className={C.secIcon} />
                  <ControlLabel className={C.subHeader}>
                    <Trans>Enter space title:</Trans>
                  </ControlLabel>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    {...{ control, name: 'title', ...register }}
                    render={({ field }) => {
                      return (
                        <TextField
                          {...field}
                          fullWidth
                          variant='filled'
                          size='small'
                          id='space-title-control'
                          autoFocus
                          className={cc([C.titleField])}
                          style={{
                            border: '1px solid',
                            borderColor: 'var(--color-subtext)',
                            borderRadius: '5px',
                          }}
                          required
                          onChange={limitChangeLength(
                            maxTitleLen,
                            field.onChange,
                          )}
                        />
                      );
                    }}
                  />
                  <FormHelperText
                    style={{ textAlign: 'right', marginBottom: '-20px' }}
                  >
                    <LengthHint value={title} maxLen={maxTitleLen} />
                  </FormHelperText>
                </Grid>
              </Grid>
            </div>
            {/* Space Type */}
            <div className={C.bodyInner}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <img src={spaceTypeIcon} className={C.secIcon} />
                  <ControlLabel className={C.subHeader}>
                    <Trans>Choose an environment:</Trans>
                  </ControlLabel>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='spaceType'
                    {...{ control, ...register }}
                    defaultValue={
                      defaultSpaceTypePreference || 'scratch_blocks'
                    }
                    render={({ field }) => (
                      <Select
                        style={{
                          textAlign: 'left',
                          color: 'var(--color-subtext)',
                        }}
                        variant='outlined'
                        size='small'
                        fullWidth
                        disabled={!!p.spaceId || !community}
                        {...field}
                      >
                        {spaceTypes.map(({ id, label, isEnabled }) => {
                          let isEnabledInCommunity = false;
                          if (id === 'p5_text') {
                            isEnabledInCommunity =
                              community?.jsEnvironmentEnabled;
                          } else if (id === 'scratch_blocks') {
                            isEnabledInCommunity =
                              community?.blockEnvironmentEnabled;
                          }

                          return (
                            <MenuItem
                              key={id}
                              value={id}
                              disabled={
                                !isEnabled ||
                                !isEnabledInCommunity ||
                                (id === 'p5_text' && !p5Enabled?.enabled)
                              }
                            >
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    )}
                  />
                </Grid>
              </Grid>
            </div>
            {/* Video/Audio */}
            <div className={C.bodyInner}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <img src={vidSettingsIcon} className={C.secIcon} />

                  <ControlLabel className={C.subHeader}>
                    <Trans>Video / Audio:</Trans>
                  </ControlLabel>
                </Grid>

                <Grid item xs={12}>
                  <ExpandButton
                    className={C.mutedOutlineBtn}
                    onClick={() => {
                      setActiveView('av-settings');
                    }}
                  >
                    {/*{avChanged ? 'Review Settings' : 'Available for everyone'}*/}
                    <Trans>Review settings</Trans>
                  </ExpandButton>
                </Grid>
              </Grid>
            </div>
            <div className={C.footer}>
              <div className={C.footerInner}>
                <LoadingButton
                  loading={isDispatching}
                  type='submit'
                  variant='contained'
                  color='primary'
                  className={C.primaryRedBtn}
                  style={{
                    width: 'calc(100% - 90px)',
                    marginTop: '15px',
                  }}
                  id='space-submit-btn'
                  onClick={() => {
                    track(
                      p.spaceId
                        ? EVENTS.UPDATE_COCO_SPACE_FINISH
                        : EVENTS.CREATE_COCO_SPACE_FINISH,
                      INTERACTIONS.CLICK,
                      {
                        step: 'primary-form',
                        communityId,
                      },
                    );
                  }}
                >
                  {p.submitLabel ??
                    (p.spaceId ? _(msg`Update`) : _(msg`Create`)) +
                    ' ' +
                    _(msg` CoCo Space`)}
                </LoadingButton>
              </div>
              {/*} <div className={C.footerControls}>
              <Controller
                {...{ control, name: 'awaitHost', ...register }}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          disabled={!canChangeNonDescriptionFields}
                        />
                      }
                      label='Participants will have to wait for the host to join first'
                      style={{
                        color: 'var(--color-subtext)',
                      }}
                    />
                  );
                }}
              />
              </div> */}
            </div>
          </div>
          <div></div>
        </>
      );
    })
    .with('participant-selection', () => {
      const maxSelectionCount = maxParticipantCount - mandatoryUserIds.length;
      return (
        <>
          <ModalHeader
            title={
              _(msg`Select Co-creators`) +
              (isHost ? '' : ' ' + _(msg`(max ${maxSelectionCount})`))
            }
            rightControls={
              <Stack justifyContent='center'>
                <Help
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setActiveView('space-onboarding');
                  }}
                />
              </Stack>
            }
            {...(showCommunitySelector
              ? {
                community,
                communities: hostableCommunities,
                setCommunity: (c) => {
                  setValue('communityId', c.id);
                },
              }
              : {})}
          />
          <CommunityCollaboratorSelector
            communityId={getValue('communityId')}
            selectedUserIds={selectedUserIds}
            maxSelectionCount={maxSelectionCount}
            mandatoryUserIds={mandatoryUserIds}
            setSelectedUserIds={setSelectedUserIds}
            maxParticipantCount={maxParticipantCount}
            onResetUsers={() => {
              setSelectedUserIds(mandatoryUserIds);
            }}
            crossGroupCollabAllowed={canCrossGroupCollab}
            canInviteCollaborators={canInviteCollaborators}
            onSubmit={() => {
              if (selectedUserIds.length > 0) {
                setSelectedUserIds(selectedUserIds);
              }
              setActiveView('primary-form');
            }}
            {...{
              register,
              control,
            }}
          />
          <div className={C.footer}></div>
        </>
      );
    })
    .with('av-settings', () => (
      <>
        <ModalHeader
          title={_(msg`Review video / audio settings`)}
          onBack={() => {
            setActiveView('primary-form');
          }}
          {...(showCommunitySelector
            ? {
              community,
            }
            : {})}
        />
        <div
          className={C.bodyInner}
          style={{ height: 'calc(100% - 60px - 60px)' }}
        >
          <Grid container spacing={2}>
            {/*<Grid item xs={12}>
              <img src={meetingSettingsIcon} className={C.secIcon} />
              <ControlLabel className={C.subHeader}>
                How are co-creators meeting?
              </ControlLabel>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                value={meetingMode}
                onChange={(e, value) => {
                  setValue('meetingMode', value);
                  Object.entries(getRecommendedAvSettings(value)).forEach(
                    ([key, val]) => {
                      setValue(key, val);
                    },
                  );
                }}
              >
                {meetingModes.map((pm) => (
                  <FormControlLabel
                    value={pm.id}
                    control={
                      <Radio disabled={!canChangeNonDescriptionFields} />
                    }
                    label={pm.label}
                  />
                ))}
              </RadioGroup>
                  </Grid>*/}
            <Grid item xs={12} display='flex' alignItems='center'>
              <img src={vidSettingsIcon} className={C.secIcon} />
              <ControlLabel className={C.subHeader}>
                <BlockedFeature
                  type={
                    canChangeVideoSettings
                      ? BLOCK_TYPE.ALLOWED
                      : BLOCK_TYPE.VIDEO_ACCESS_DISABLED
                  }
                >
                  <Trans>Set video access</Trans>{' '}
                  <Controller
                    name='vidEnabledForParticipants'
                    {...{ control, ...register }}
                    render={({ field }) => (
                      <Switch
                        id='vid-enabled-ctrl2'
                        {...field}
                        checked={!canChangeVideoSettings ? false : field.value}
                        className={C.greenSwitch}
                        disabled={
                          !canChangeNonDescriptionFields ||
                          !canChangeVideoSettings
                        }
                      />
                    )}
                  />
                </BlockedFeature>
              </ControlLabel>
            </Grid>
            <Grid item xs={12} display='flex' alignItems='center'>
              <img src={audioSettingsIcon} className={C.secIcon} />
              <ControlLabel className={C.subHeader}>
                <BlockedFeature
                  type={
                    canChangeAudioSettings
                      ? BLOCK_TYPE.ALLOWED
                      : BLOCK_TYPE.AUDIO_ACCESS_DISABLED
                  }
                >
                  <Trans>Set audio access</Trans>{' '}
                  <Controller
                    name='micEnabledForParticipants'
                    {...{ control, ...register }}
                    render={({ field }) => (
                      <Switch
                        id='mic-enabled-ctrl2'
                        {...field}
                        checked={!canChangeAudioSettings ? false : field.value}
                        className={C.greenSwitch}
                        disabled={
                          !canChangeNonDescriptionFields ||
                          !canChangeAudioSettings
                        }
                      />
                    )}
                  />
                </BlockedFeature>
              </ControlLabel>
            </Grid>
          </Grid>
          <div style={{ marginTop: '1rem', color: 'var(--color-subtext)' }}>
            <Trans>
              *Audio is recommended only if co-creators are connecting remotely.
              You can update this setting anytime.
            </Trans>
          </div>
        </div>
        <div className={C.modalFooter}>
          <div style={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            size='small'
            onClick={() => {
              setActiveView('primary-form');
            }}
          >
            <Trans>Done</Trans>
          </Button>
        </div>
      </>
    ))
    .otherwise(() => null);

  return (
    <CloseableDialog
      onClose={() => {
        track(EVENTS.CLOSE_MODAL, INTERACTIONS.CLICK, {
          modal: p.spaceId ? 'edit-space' : 'create-space',
        });
        p.onClose?.();
      }}
      className={C.dialog}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <form
          className={cc([C.container])}
          onSubmit={handleSubmit(performSubmit)}
          autoComplete='off'
        // ref={fixContainerBounds}
        >
          {/* <div className={C.sidebar}>{primaryFields}</div> */}
          <div className={C.body}>{auxFields}</div>
        </form>
      </LocalizationProvider>
    </CloseableDialog>
  );

  function fixContainerBounds(containerEl: HTMLFormElement | null) {
    if (!containerEl) return;
    const containerBounds = containerEl.getBoundingClientRect();
    containerEl.style.height = containerBounds.height + 'px';
  }
}

const ModalHeader = (p: {
  title: string | ReactNode;
  onBack?: () => void;
  community?: CommunityDetails;
  communities?: CommunityDetails[];
  setCommunity?: (c: CommunityDetails) => any;
  rightControls?: ReactNode;
}) => {
  return (
    <header className={cc([C.sectionPrimaryHeader, C.modalHeader])}>
      <div className={C.modalHeaderInner}>
        <Stack component='div' direction='row'>
          {p.onBack && (
            <div className={C.modalHeaderCtrl}>
              <Button
                size='small'
                sx={{
                  marginRight: '0.5rem',
                }}
                className={C.primaryIconBtn}
                onClick={p.onBack}
              >
                <ChevronLeftIcon />
              </Button>
            </div>
          )}
          {p.community ? (
            <CommunityIcon
              id={p.community.id}
              avatarUrl={p.community.avatarUrl}
              name={p.community.name}
              isActive={false}
              showSelector={Boolean(p.communities)}
              communities={p.communities}
              setCommunity={p.setCommunity}
              style={{
                boxShadow: '0 0 3px rgba(0,0,0,0.3)',
              }}
            />
          ) : (
            <div></div>
          )}
        </Stack>
        <div>
          <div>{p.title}</div>
        </div>
        <div>
          <div>{p.rightControls}</div>
        </div>
      </div>
    </header>
  );
};

export const GroupChip = (p: {
  label: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
  onDeselect?: () => void;
}) => {
  const { label, isSelected, onSelect } = p;
  return (
    <ToggleChip
      sx={{
        borderRadius: '0.3rem',
        borderWidth: '1px !important',
      }}
      label={label}
      isSelected={isSelected}
      onSelect={onSelect}
      onDeselect={p.onDeselect}
    />
  );
};

const CommunityCollaboratorSelector = (p: {
  communityId: string;
  maxSelectionCount: number;
  mandatoryUserIds: string[];
  selectedUserIds: string[];
  onResetUsers?: () => void;
  setSelectedUserIds?: (update: (userIds: string[]) => string[]) => void;
  onSubmit?: () => void;
  register: UseFormRegister<any>;
  control: Control<any, any>;
  maxParticipantCount: number;
  crossGroupCollabAllowed?: boolean;
  canInviteCollaborators?: boolean;
}) => {
  const { _ } = useLingui();
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const { userProfile } = useUserProfile();
  const { community } = useCommunityDetails({
    communityId: p.communityId,
  });
  const [isDispatching, setDispatching] = useState(false);
  const cMembers = community?.members ?? [];
  const [countExceededErrVisible, setCountExceededErrVisible] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<CommunityUserGroup | null>(
    null,
  );

  const isHost = CMR.isHost(community?.roles ?? []);

  const track = useTrack();

  const toggleSelectedGroup = (group: CommunityUserGroup) => () => {
    setSelectedGroup((curGroup) => {
      if (curGroup && group.id === curGroup.id) {
        return null;
      } else {
        return group;
      }
    });
  };

  const members = useMemo(() => {
    const communityMembers = cMembers.filter((m) => {
      return m.isApproved && m.user;
    });
    // Place mandatory users at the top
    const mandatoryMembers = communityMembers.filter((m) => {
      return p.mandatoryUserIds.indexOf(m.user!.id) >= 0;
    });
    const otherMembers = communityMembers.filter((m) => {
      return p.mandatoryUserIds.indexOf(m.user!.id) < 0;
    });
    return mandatoryMembers.concat(otherMembers);
  }, [cMembers]);

  const userToGroupSets = useMemo(() => {
    const userToGroupSets: Record<string, Set<string>> = {};
    community?.groups.forEach((g) => {
      g.userIds.forEach((uid) => {
        if (!userToGroupSets[uid]) {
          userToGroupSets[uid] = new Set();
        }
        userToGroupSets[uid].add(g.id);
      });
    });
    return userToGroupSets;
  }, [community]);

  const listedMembers = useMemo(() => {
    return members.filter((m) => {
      if (!p.crossGroupCollabAllowed) {
        // Check if there is this user has atleast one group in common with the current user
        if (!userProfile) return false;
        const userGroupIds = userToGroupSets[userProfile.id];
        const memberGroupIds = userToGroupSets[m.user!.id];
        if (!userGroupIds || !memberGroupIds) return false;
        let hasCommonGroup = false;
        userGroupIds.forEach((gid) => {
          if (memberGroupIds.has(gid)) {
            hasCommonGroup = true;
          }
        });
        if (!hasCommonGroup) return false;
      }

      return (
        !selectedGroup ||
        (m.user && selectedGroup.userIds.indexOf(m.user.id) >= 0)
      );
    });
  }, [members, selectedGroup]);

  const searchFieldRef = useRef<HTMLDivElement | null>(null);

  const selectUsers = (userIds: string[]) => {
    p.setSelectedUserIds?.((list) => {
      const next = uniq(list.concat(userIds));
      if (next.length > p.maxParticipantCount) {
        setCountExceededErrVisible(true);
        return list;
      }
      return next;
    });
  };

  const deselectUsers = (userIds: string[]) => {
    const removableIds = new Set(userIds);
    p.mandatoryUserIds.forEach((id) => {
      removableIds.delete(id);
    });
    p.setSelectedUserIds?.((list) =>
      list.filter((uid) => !removableIds.has(uid)),
    );
  };

  return (
    <div className={C.memberSelector}>
      {!isEmpty(community?.groups) ? (
        <Stack direction='row' alignItems='center' spacing='1rem' mb={2}>
          <Box
            sx={{
              color: 'var(--color-subtext)',
            }}
          >
            <Trans>Groups</Trans>:
          </Box>
          <ScrollableContainer
            elements={
              <>
                {p.crossGroupCollabAllowed && (
                  <GroupChip
                    label={_(msg`All`)}
                    isSelected={!selectedGroup}
                    onSelect={() => setSelectedGroup(null)}
                  />
                )}
                {community?.groups
                  .filter(
                    (g) =>
                      p.crossGroupCollabAllowed ||
                      g.userIds.indexOf(userProfile?.id) >= 0,
                  )
                  .map((g) => (
                    <GroupChip
                      label={g.name}
                      isSelected={!!selectedGroup && selectedGroup.id === g.id}
                      onSelect={toggleSelectedGroup(g)}
                    />
                  ))}
              </>
            }
            sx={{
              margin: '0.5rem 0rem',
            }}
          />
        </Stack>
      ) : (
        <></>
      )}
      <Stack direction='row' alignItems='center' gap={1}>
        <div className={C.memberSearchField}>
          <SearchInputField
            fullWidth
            onChange={(e) => {
              setUserSearchQuery(e.target.value);
            }}
            onClear={() => {
              setUserSearchQuery('');
            }}
            placeholder={_(msg`Search members`)}
            className={C.memberSearchField}
          // tags={members.map(({ user }): ReactNode => {
          //   if (!user) return null;
          //   if (p.selectedUserIds.indexOf(user.id) < 0) return null;
          //   return (
          //     <Chip
          //       label={qualifiedUserName(user)}
          //       size='small'
          //       onDelete={() => {
          //         deselectUsers([user.id]);
          //       }}
          //     />
          //   );
          // })}
          />
        </div>
      </Stack>
      <AddMembersButton
        variant='text'
        theme='contained'
        communityId={p.communityId}
        customBtn={
          <Button
            fullWidth
            variant='text'
            size='small'
            startIcon={<PersonAddAlt sx={{
              fontSize: '1.5rem',
              marginRight: '0.3rem',
            }} color='var(--color-subtext)' />}
            onClick={() => {
              p.onAddMembers?.();
            }}
            sx={{
              borderStyle: 'dotted',
              borderColor: 'var(--color-border)',
              borderWidth: '1px',
              marginTop: '0.5rem',
              backgroundColor: 'rgb(250, 247, 254)',
              color: 'var(--color-subtext)'
            }}
          >
            Not in the list? Add members
          </Button>
        }
      />
      <Box mt={1} />

      {
        p.selectedUserIds.length > 0 && (
          <ScrollableContainer
            elements={members.map(({ user }): ReactNode => {
              if (!user) return null;
              if (p.selectedUserIds.indexOf(user.id) < 0) return null;
              return (
                <Chip
                  label={
                    user.id === userProfile?.id
                      ? _(msg`You`)
                      : qualifiedUserName(user)
                  }
                  size='small'
                  sx={{
                    backgroundColor: 'var(--color-selected)',
                    border: '1px solid var(--color-border)',
                  }}
                  onDelete={
                    p.mandatoryUserIds.includes(user.id)
                      ? undefined
                      : () => {
                        deselectUsers([user.id]);
                      }
                  }
                />
              );
            })}
          />
        )
      }
      {
        countExceededErrVisible && (
          <div
            style={{
              fontSize: 'var(--font-size-subtext)',
              color: 'var(--color-coco-pink)',
            }}
          >
            {!isHost ? (
              <Trans>
                You can only add up to {p.maxSelectionCount} co-creators.
              </Trans>
            ) : (
              <Trans>You cannot add more co-creators in this space.</Trans>
            )}
          </div>
        )
      }
      <div className={C.memberSelectorList}>
        <div className={C.userListContainer}>
          <UserList
            communityId={p.communityId}
            members={listedMembers}
            selectedUserIds={p.selectedUserIds}
            mandatoryUserIds={p.mandatoryUserIds}
            query={userSearchQuery}
            showControls={false}
            onSelect={(userId, isSelected) => {
              if (isSelected) {
                selectUsers([userId]);
              } else {
                deselectUsers([userId]);
              }
            }}
            checkboxOrientation='right'
            showEmail={false}
            singleLine={true}
            subText
          />
        </div>
      </div>
      <div className={C.modalFooter}>
        {p.canInviteCollaborators && p.crossGroupCollabAllowed && (
          <Controller
            name='openForOthers'
            {...p.register}
            control={p.control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={_(msg`Open for others to join`)}
              />
            )}
          />
        )}
        <div style={{ flexGrow: 1 }} />
        {/* <Button
          variant='text'
          size='small'
          onClick={() => {
            p.onResetUsers?.();
          }}
          style={{ marginRight: '5px' }}
        >
          Reset to default
        </Button> */}
        <LoadingButton
          loading={isDispatching}
          variant='contained'
          size='small'
          onClick={(e) => {
            track(EVENTS.CREATE_SPACE_NEXT, INTERACTIONS.CLICK, {
              step: 'participant-selection',
            });
            handleSubmit();
          }}
        >
          <Trans>Next</Trans>
        </LoadingButton>
      </div>
    </div >
  );

  async function handleSubmit() {
    p.onSubmit?.();
  }
};

const LengthHint = (p: {
  value: string | undefined | null;
  maxLen: number;
}) => {
  const curLen = p.value ? p.value.length : 0;
  return (
    <span>
      {curLen} / {p.maxLen}
    </span>
  );
};

export const getRecommendedAvSettings = (meetingMode: string) => {
  return match(meetingMode)
    .with('everyone_same_room', () => ({
      vidEnabledForParticipants: true,
      micEnabledForParticipants: false,
    }))
    .with(P.union('everyone_online', 'mix_online'), () => ({
      vidEnabledForParticipants: true,
      micEnabledForParticipants: true,
    }))
    .otherwise(() => ({}));
};

const selectHostableCommunities = (communities: CommunityWithAggregates[]) => {
  return communities.filter((it) => {
    return it.isActive && CMR.isGTE(it.currentUserRoles, 'MEMBER');
  });
};

const getMemberCountText = (
  community: CommunityDetails | null | undefined,
  selfUser: { id: string },
  didSelect: boolean,
  selectedUserIds: string[],
  mandatoryUserIds: string[],
  openForOthers?: boolean,
  canInviteCollaborators?: boolean,
  crossGroupCollabAllowed?: boolean,
) => {
  const { _ } = useLingui();
  if (!canInviteCollaborators) {
    return <Trans>Disabled by admin</Trans>;
  }
  if (!didSelect || !community) {
    return <Trans>Select co-creators</Trans>;
  }

  const count = selectedUserIds.length;
  if (count === 0 || (count === 1 && selectedUserIds[0] === selfUser.id)) {
    if (
      canInviteCollaborators &&
      crossGroupCollabAllowed &&
      openForOthers &&
      selectedUserIds.includes(selfUser.id)
    ) {
      return <Trans>You | Others can join</Trans>;
    }
    return <Trans>0 selected</Trans>;
  }

  const users: string[] = [];

  for (const user of mandatoryUserIds) {
    const member = community?.members?.find((m) => m.user?.id === user);
    if (member?.user) {
      if (member?.user?.id === selfUser.id) {
        users.push(_(msg`You`));
        continue;
      }
      users.push(`${qualifiedUserName(member.user)}`);
    }
  }

  for (const user of selectedUserIds) {
    if (mandatoryUserIds.indexOf(user) >= 0) continue;
    const member = community?.members?.find((m) => m.user?.id === user);
    if (member?.user) {
      if (member?.user?.id === selfUser.id) {
        users.push(_(msg`You`));
        continue;
      }
      users.push(`${qualifiedUserName(member.user)}`);
    }
  }

  return users.join(' | ');
};

const defaultDuration: TimeDuration = {
  duration: 1,
  unit: 'hours',
};

const normalizeDuration = (
  timeDuration: TimeDuration | null,
  isPresent?: boolean,
): TimeDuration | null => {
  if (!timeDuration && isPresent) {
    return defaultDuration;
  }
  if (!timeDuration) return timeDuration;
  if (timeDuration.duration < 0) return defaultDuration;
  return timeDuration;
};
